import { useEffect, useMemo, useState } from 'react';
import {
  ActualVsPredictedDataInitial,
  CptbData,
  DecayData,
  ModelOverviewData,
  RoasData,
  SatData
} from '@blisspointmedia/bpm-types/dist/MMM';
import { TEST_COMPANIES } from '@blisspointmedia/bpm-types/dist/TestCompanies';
import { medianOrMean, MedianOrMean } from '@blisspointmedia/bpm-types/dist/MMMUtils';
import { FullPageSpinner } from '@Components/Spinner';
import { ReturnOnAdSpend } from '@Components/Charts/ReturnOnAdSpend/ReturnOnAdSpend';
import { findGroupByOptions } from '@Components/Charts/ModelResults/DataFormatters';
import {
  FormattedModelRunOptions,
  TINUITI_TEST_HOME_SERVICES,
  TINUITI_TEST_HOME_SERVICES_PROXY
} from '@Pages/MMM/mmm-utils';
import { ModelOverview } from './ModelOverview';
import { ChannelPlatformTacticBrand as CPTB } from './ChannelPlatformTacticBrand';
import { SatAndDecay } from './SaturationAndDecay';
import { useSetError } from '../../../redux/modals';
import { awaitJSON, fetchCometSelfInvoke, MetricsLambdaFetch } from '../../../utils/fetch-utils';
import { useLocationCompany } from '../../../utils/hooks/useLocationCompany';

interface ModelResultsProps {
  showActualizedData: boolean;
  disabledPlatform: boolean | undefined;
  groupBy: string;
  setGroupBy: (val: string) => void;
  groupByOptions: { label: string; value: string }[];
  setGroupByOptions: (options: { label: string; value: string }[]) => void;
  kpi: string;
  modelRun: FormattedModelRunOptions;
}

export const ModelResults = ({
  disabledPlatform,
  groupBy,
  setGroupBy,
  groupByOptions,
  setGroupByOptions,
  kpi,
  modelRun,
  showActualizedData
}: ModelResultsProps) => {
  let company = useLocationCompany() ?? '';
  if (TEST_COMPANIES.includes(company)) {
    company = 'test';
  } else if (company === TINUITI_TEST_HOME_SERVICES) {
    company = TINUITI_TEST_HOME_SERVICES_PROXY;
  }
  const [cptbData, setCptbData] = useState<CptbData[]>([]);
  const [modelOverviewData, setModelOverviewData] = useState<ModelOverviewData[]>([]);
  const [overallRoasCpa, setOverallRoasCpa] = useState<number>();
  const [cptbDataDirect, setCptbDataDirect] = useState<CptbData[]>([]);
  const [roasData, setRoasData] = useState<RoasData[]>([]);
  const [roasDataDirect, setRoasDataDirect] = useState<RoasData[]>([]);
  const [actualVsPredictedData, setActualVsPredictedData] = useState<
    ActualVsPredictedDataInitial[]
  >([]);
  const [satData, setSatData] = useState<SatData[]>([]);
  const [satDataDirect, setSatDataDirect] = useState<SatData[]>([]);
  const [decayData, setDecayData] = useState<DecayData[]>([]);
  const [twoStageData, setTwoStageData] = useState<any[]>();
  const [loading, setLoading] = useState<boolean>(true);
  const [omitDarkInputs, setOmitDarkInput] = useState<boolean>(true);
  const setError = useSetError();
  const [centralMeasure, setCentralMeasure] = useState<MedianOrMean>(MedianOrMean.MEDIAN);

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true);
        const realOrTestKpi = modelRun.kpis.find(k => k.value === kpi)?.realKpi ?? kpi;
        const params = {
          company,
          branch: modelRun.branch,
          kpi: realOrTestKpi,
          date: modelRun.date,
          outputFileVersion: modelRun.output_file_version.toString()
        };

        const [
          modelOverviewRes,
          actualVsPredictedRes,
          decayCurvesRes,
          responseCurvesRes,
          marginalDecisionRes,
          spendEffectOverTimeRes,
          twoStageResult
        ] = await Promise.all([
          MetricsLambdaFetch('/getModelOverviewData', { params }),
          MetricsLambdaFetch('/getActualVsPredictedData', { params }),
          MetricsLambdaFetch('/getDecayCurvesData', { params }),
          MetricsLambdaFetch('/getResponseCurvesData', { params }),
          MetricsLambdaFetch('/getMarginalDecisionData', { params }),
          MetricsLambdaFetch('/getSpendEffectOverTimeData', { params }),
          fetchCometSelfInvoke<typeof params, any>(MetricsLambdaFetch, '/getTwoStageData', params, {
            method: 'GET'
          })
        ]);

        const [
          modelOverviewJson,
          actualVsPredictedJson,
          decayCurvesData,
          responseCurvesData,
          marginalDecisionData,
          spendEffectOverTimeData
        ] = await Promise.all([
          awaitJSON(modelOverviewRes),
          awaitJSON(actualVsPredictedRes),
          awaitJSON(decayCurvesRes),
          awaitJSON(responseCurvesRes),
          awaitJSON(marginalDecisionRes),
          awaitJSON(spendEffectOverTimeRes)
        ]);

        setModelOverviewData(modelOverviewJson);
        setActualVsPredictedData(actualVsPredictedJson);
        setDecayData(decayCurvesData);
        setSatData(responseCurvesData.actualized);
        setSatDataDirect(responseCurvesData.raw);
        setRoasData(marginalDecisionData.actualized);
        setRoasDataDirect(marginalDecisionData.raw);

        const cptbActualized = spendEffectOverTimeData.actualized;
        setCptbData(spendEffectOverTimeData.actualized);
        setCptbDataDirect(spendEffectOverTimeData.raw);

        // Set group by options based on CPTB data
        const nextGroupByOptions = findGroupByOptions(cptbActualized);
        setGroupByOptions(nextGroupByOptions);

        setTwoStageData(twoStageResult?.twoStageData || twoStageResult);
      } catch (e) {
        setError({ message: e.message, reportError: e });
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [company, modelRun, kpi, setError, setGroupByOptions]);

  useEffect(() => {
    if (roasData[0]) {
      setCentralMeasure(medianOrMean(roasData[0]));
    }
  }, [roasData]);

  const kpiType = useMemo(() => {
    const kpiTypeRow = modelOverviewData.find(row => row.parameter === 'kpi_type');
    return kpiTypeRow ? kpiTypeRow.value : '';
  }, [modelOverviewData]);

  // Only display the "Omit Dark Inputs" toggle if some of the data has dark_last_quarter
  const renderOmitDarkInputsToggle = useMemo(
    () =>
      roasData.some(row => row.dark_last_quarter) ||
      roasDataDirect.some(row => row.dark_last_quarter) ||
      satData.some(row => row.dark_last_quarter) ||
      satDataDirect.some(row => row.dark_last_quarter) ||
      decayData.some(row => row.dark_last_quarter),
    [decayData, roasData, roasDataDirect, satData, satDataDirect]
  );

  const filteredRoasData = useMemo(
    () => roasData.filter(row => !row.dark_last_quarter),
    [roasData]
  );

  const filteredRoasDataDirect = useMemo(
    () => roasDataDirect.filter(row => !row.dark_last_quarter),
    [roasDataDirect]
  );

  const roasDataToUse = useMemo(() => {
    const source = showActualizedData ? roasData : roasDataDirect;
    const filtered = showActualizedData ? filteredRoasData : filteredRoasDataDirect;
    return omitDarkInputs ? filtered : source;
  }, [
    filteredRoasData,
    filteredRoasDataDirect,
    omitDarkInputs,
    roasData,
    roasDataDirect,
    showActualizedData
  ]);

  const filteredSatData = useMemo(() => satData.filter(row => !row.dark_last_quarter), [satData]);

  const filteredSatDataDirect = useMemo(
    () => satDataDirect.filter(row => !row.dark_last_quarter),
    [satDataDirect]
  );

  const satDataToUse = useMemo(() => {
    const source = showActualizedData ? satData : satDataDirect;
    const filtered = showActualizedData ? filteredSatData : filteredSatDataDirect;
    return omitDarkInputs ? filtered : source;
  }, [
    filteredSatData,
    filteredSatDataDirect,
    omitDarkInputs,
    satData,
    satDataDirect,
    showActualizedData
  ]);

  const filteredDecayData = useMemo(
    () => decayData.filter(row => !row.dark_last_quarter),
    [decayData]
  );

  const decayDataToUse = useMemo(
    () => (omitDarkInputs ? filteredDecayData : decayData),
    [decayData, filteredDecayData, omitDarkInputs]
  );

  if (loading || !kpiType) {
    return <FullPageSpinner />;
  }

  return (
    <div>
      <ModelOverview
        company={company}
        cptbDataDirect={cptbDataDirect}
        cptbData={cptbData}
        disabledPlatform={disabledPlatform}
        groupBy={groupBy}
        modelOverviewData={modelOverviewData}
        kpi={kpi}
        kpiType={kpiType}
        overallRoasCpa={overallRoasCpa}
        actualVsPredictedData={actualVsPredictedData}
        showActualizedData={showActualizedData}
      />
      <CPTB
        kpi={kpi}
        cptbData={showActualizedData ? cptbData : cptbDataDirect}
        disabledPlatform={disabledPlatform}
        groupBy={groupBy}
        setGroupBy={setGroupBy}
        groupByOptions={groupByOptions}
        kpiType={kpiType}
        setOverallRoasCpa={setOverallRoasCpa}
        showActualizedData={showActualizedData}
        twoStageData={twoStageData}
        modelDate={modelRun.date}
      />
      <ReturnOnAdSpend
        disabledPlatform={disabledPlatform}
        kpiType={kpiType}
        data={roasDataToUse}
        renderOmitDarkInputsToggle={renderOmitDarkInputsToggle}
        omitDarkInputs={omitDarkInputs}
        setOmitDarkInputs={setOmitDarkInput}
        centralMeasure={centralMeasure}
      />
      <SatAndDecay
        disabledPlatform={disabledPlatform}
        kpiType={kpiType}
        satData={satDataToUse}
        decayData={decayDataToUse}
        renderOmitDarkInputsToggle={renderOmitDarkInputsToggle}
        omitDarkInputs={omitDarkInputs}
        setOmitDarkInput={setOmitDarkInput}
        centralMeasure={centralMeasure}
      />
    </div>
  );
};
